import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/1dde86ed/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const Caption = makeShortcode("Caption");
const DoDontExample = makeShortcode("DoDontExample");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`A form is a group of related input controls that allows users to provide data or configure options. Forms can be simple or complex, and may be presented as dedicated pages, side panels, or dialogs depending on the use case and the situation.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">Overview</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Building a form</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Behavior</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Designing a form</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Variants</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Related</AnchorLink>
  <AnchorLink mdxType="AnchorLink">References</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Feedback</AnchorLink>
    </AnchorLinks>
    <h2>{`Overview`}</h2>
    <h3>{`When to use`}</h3>
    <p>{`Forms are incredibly common in user interfaces and their design and usage continues to evolve as input methods get smarter and more and more people use mobile and tablet devices. You might design a form for a user to:`}</p>
    <ul>
      <li parentName="ul">{`Sign up for / log into an account`}</li>
      <li parentName="ul">{`Register for a service`}</li>
      <li parentName="ul">{`Reconfigure settings, (e.g. enabling notifications)`}</li>
      <li parentName="ul">{`Take a survey`}</li>
      <li parentName="ul">{`Purchase a product`}</li>
      <li parentName="ul">{`Provide feedback`}</li>
    </ul>
    <h3>{`Respect the user`}</h3>
    <p>{`Forms are meant to gather information and guide people with as little fuss as possible. To allow users to scan and complete the form quickly, forms should:`}</p>
    <ul>
      <li parentName="ul">{`Respect the user’s GDPR and other privacy regulations by only asking for information that is absolutely necessary.`}</li>
      <li parentName="ul">{`Group related tasks under section titles to provide more context and make the interface easier to scan.`}</li>
      <li parentName="ul">{`Follow a logical, predictable order—e.g first name first, last name second.`}</li>
      <li parentName="ul">{`Allow users to stay with a single interaction method for as long as possible (i.e. do not make users shift from keyboard to mouse numerous times in a single form).`}</li>
      <li parentName="ul">{`When designing be mindful of password managers and browser capabilities that populate data for users.`}</li>
      <li parentName="ul">{`Progressively disclose additional inputs only as they become relevant; see the `}<a parentName="li" {...{
          "href": "#designing-for-longer-forms"
        }}>{`Designing for longer forms`}</a>{` section below.`}</li>
    </ul>
    <h3>{`Anatomy of a form`}</h3>
    <p>{`Forms are comprised of some or all of the following elements.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "110.19021739130434%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAWCAYAAADAQbwGAAAACXBIWXMAAAsSAAALEgHS3X78AAACdUlEQVQ4y41VS67TQBB8F+AIiA0SiFOxRwiBOAUHYQUCFhwBxE+sHhKsHkleEjvB/78908XUOP4mD2Kp0p6op7p7qnt8ISI496Fvh249fy74U9c14jhGFEUoigJpmiLLMrvm/1xrrY+ITz2WkCTr9Rqr1Qq73c6+d1gsFri+vrY+Td2gaZoJ8Y2EjuPAdVy4rgvHYGvW+/0ergnguA7W7hYbY5cmQFmW0yPQQ8aWkOWwbEbXSkFqBW3RWNhj2Qaolt6NmXX/92fYZRZ6PoooQR52iJGnGeLfDuKrLUpV24os8hwaguLnBvUuGjJUJis/8G2JIYUxJeVmQ16aTQZ8L5rKIs9y5PkA7q23IVRaDIQsNUmS3oFHoNUIdq1aaN2Dvixelw2kUVNCihKGoW0Rkv8P9GNbNYY0+36FahNMCdl3zJBWzTI5BSpNjAXqRSGh7/sIgsBaRh6DgXohDmBgG1yrifI9IcudgwFoOS1zsGzartFP9uEYdKyq6iizMVoRTxByI7NhxC47z/NGE3HepdETMhLnls1Nu9lsrCXpPhQECXpiwXDjdO9HolBVZtmB68FJW3TqHlgPaMnqfQyVFNPry/cDC8+Mnk8xjLqhVTluYY6BimdmLKsoQxmlKM1octbzyxVqNxqLosy8RkjiwCKNQ2vjyLc2CgN7xgRJU9MuaZba5p5W010OSuAlA/4Y+Kk5u6y1RX3YMLu15+gz/PhLcPeJ4P4zwb2nggfPBXceC24/Etx6qPHivXFabZH8WLfEWo5Unlywu0jw5ovg3TfB26+tff1Z8OqT4OUHweVS2FtQeXX0bZl/Xy7O/UjJP6798fMX68qo3q88s7cAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Basic form with anatomy callouts",
            "title": "Basic form with anatomy callouts",
            "src": "/static/3a667e17c7acb97c8b6001249f1f8f5a/3cbba/Elements.png",
            "srcSet": ["/static/3a667e17c7acb97c8b6001249f1f8f5a/7fc1e/Elements.png 288w", "/static/3a667e17c7acb97c8b6001249f1f8f5a/a5df1/Elements.png 576w", "/static/3a667e17c7acb97c8b6001249f1f8f5a/3cbba/Elements.png 1152w", "/static/3a667e17c7acb97c8b6001249f1f8f5a/392b1/Elements.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Labels:`}</strong>{` Input labels helps users understand what the corresponding inputs mean.`}</li>
      <li parentName="ol"><strong parentName="li">{`Text inputs:`}</strong>{` Enable users to input free-form text.`}</li>
      <li parentName="ol"><strong parentName="li">{`Data inputs:`}</strong>{` Information can be entered through a variety of non-free form input fields as well, (e.g. checkboxes, radio buttons, dropdowns and selects, file uploader, date pickers, toggles, etc.) Visit the individual component pages for in depth details of their specific states and visuals.`}</li>
      <li parentName="ol"><strong parentName="li">{`Help:`}</strong>{` Provides in-context guidance like tooltips, placeholder text, or helper text, to assist the user in providing the right information.`}</li>
      <li parentName="ol"><strong parentName="li">{`Buttons:`}</strong>{` Allows users to submit or exit a form.`}</li>
    </ol>
    <h2>{`Building a form`}</h2>
    <h3>{`Labels`}</h3>
    <p>{`Concise labels for text and data inputs help users understand what information is being requested of them.`}</p>
    <ul>
      <li parentName="ul">{`Use sentence-style capitalization for all text elements, except for product names and proper nouns. Sentence-style means only the first word of each sentence is capitalized.`}</li>
      <li parentName="ul">{`Although they may be formatted differently, all input components need labels.`}</li>
      <li parentName="ul">{`Labels should clearly state the required input.`}</li>
      <li parentName="ul">{`Do not use colons after label names.`}</li>
      <li parentName="ul">{`Labels are not help text; be succinct. Use one to three words only.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAc0lEQVQoz51RAQqAIBDz/08NDRLBOzOXJRdGJtlgzNPzmFOllJCQcehPlutFlRSlxHUoDV9Q96raYVw8vHWPwUMO64EbrZgnDW0MTKa1Fs65LmOM7SeLsicQZxKBmRFC6LLpUAKs1yO4ZXhuyC8P5PaW4Q7s3ypOIbXomQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of labels",
            "title": "Example of labels",
            "src": "/static/505ec3d335f7fc7357bdb1d1eae6a1a0/3cbba/labels.png",
            "srcSet": ["/static/505ec3d335f7fc7357bdb1d1eae6a1a0/7fc1e/labels.png 288w", "/static/505ec3d335f7fc7357bdb1d1eae6a1a0/a5df1/labels.png 576w", "/static/505ec3d335f7fc7357bdb1d1eae6a1a0/3cbba/labels.png 1152w", "/static/505ec3d335f7fc7357bdb1d1eae6a1a0/392b1/labels.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Top-aligned labels`}</h4>
    <p>{`Top-aligned labels are Carbon’s default (vs. left-aligned labels) and the only label arrangement currently offered. Top-aligned labels provide a consistent left edge, a close proximity between label and input, and are good for scannability and quick form completion.`}</p>
    <p>{`Advantages:`}</p>
    <ul>
      <li parentName="ul">{`Top-alignment enables quick completion.`}</li>
      <li parentName="ul">{`The label length has room to extend or vary, (e.g. other languages).`}</li>
      <li parentName="ul">{`When users are entering familiar content and are less likely to make data entry errors, top-alignment is ideal.`}</li>
      <li parentName="ul">{`This arrangement is best when fewer form fields need to be presented.`}</li>
    </ul>
    <h4>{`Optional vs. mandatory`}</h4>
    <p>{`All fields in a form are assumed required, with optional fields being tagged as so in the label. Although advise avoiding an excess of optional fields, if the situation arises, we recommend devoting an entire section to optional fields to avoid excessive repetition.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAZUlEQVQoz72RjQqAIAyEff83TWmUjMSlu35IMIIKiQ4+Ng42js1glaqi1BbqHQaHitGietacEuaMKQRwP4AtIcsMfZH8krA2iQi2s3DOwY8ezHxLSul+oYggxriz9U/8fMMPvrwArZYqJ7zzcg4AAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of input field with optional label",
            "title": "Example of input field with optional label",
            "src": "/static/67df2d0f90dcf0d6cf8d7ac2e5001d8b/3cbba/optional_v_mandatory.png",
            "srcSet": ["/static/67df2d0f90dcf0d6cf8d7ac2e5001d8b/7fc1e/optional_v_mandatory.png 288w", "/static/67df2d0f90dcf0d6cf8d7ac2e5001d8b/a5df1/optional_v_mandatory.png 576w", "/static/67df2d0f90dcf0d6cf8d7ac2e5001d8b/3cbba/optional_v_mandatory.png 1152w", "/static/67df2d0f90dcf0d6cf8d7ac2e5001d8b/392b1/optional_v_mandatory.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Text inputs`}</h3>
    <p>{`Free-form text inputs are the most commonly used components in forms.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "45.80357142857143%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAAAiklEQVQoz62RCQrFIBBDvf9l3RXXKSmM2K9QC38goJIEfYreO70NPKc+wYucM9VaqbU2Crhk3qeUbh8LZ5zBjEJrLWmtyXtPIYRbMcZHGWSMISklKaWG3zn3LNw9b3fDExxiZ/4tfdOcETObUsrg+EXILgyZDViCyanYvxTiEwCYIX8RMkvhP4S5APSFxd6Cbd2qAAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of text input types",
        "title": "Example of text input types",
        "src": "/static/1ae70669dca950e6193e4e05767aee6a/3cbba/Text_inputs.png",
        "srcSet": ["/static/1ae70669dca950e6193e4e05767aee6a/7fc1e/Text_inputs.png 288w", "/static/1ae70669dca950e6193e4e05767aee6a/a5df1/Text_inputs.png 576w", "/static/1ae70669dca950e6193e4e05767aee6a/3cbba/Text_inputs.png 1152w", "/static/1ae70669dca950e6193e4e05767aee6a/0b124/Text_inputs.png 1728w", "/static/1ae70669dca950e6193e4e05767aee6a/99571/Text_inputs.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <br />
    <p><strong parentName="p">{`Deciding what to use`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Control`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Context`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/text-input/usage"
            }}>{`Text input`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To capture several words maximum`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Names; phone numbers; addresses`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/textinput--toggle-password-visibility"
            }}>{`Password`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To collect private data by hiding the characters`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Passwords, Social Security Numbers (SSN), PINs, credit card information`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/textarea--default"
            }}>{`Text area`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To capture multiple lines of text`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Feedback; support requests`}</td>
        </tr>
      </tbody>
    </table>
    <h4>{`Best practices`}</h4>
    <ul>
      <li parentName="ul">{`The field widths should reflect the intended length of the content while still aligning to the responsive column or mini unit grid.`}</li>
      <li parentName="ul">{`Make sure users can enter their information at smaller screen sizes.`}</li>
      <li parentName="ul">{`Truncate when an input is too long to be fully displayed in the field.`}</li>
      <li parentName="ul">{`Pre-populate known values when possible, e.g. a default IP address.`}</li>
      <li parentName="ul">{`The first required input field in a form should receive focus on presentation to a user.`}</li>
    </ul>
    <h3>{`Data inputs`}</h3>
    <p>{`These controls enable users to provide input on forms by selecting from a set of pre-determined options or a limited range of values. Carbon provides a variety of data input components that enable a user to make a selection. Each component was created to serve a specific use case.`}</p>
    <h4>{`Selection controls`}</h4>
    <p>{`Selection controls offer users a selection from pre-determined options. When designing, consider how many options you need to present as well as how many items the user may need to select. These considerations will determine which component to use. Common selection controls include: checkboxes, radio buttons, file uploaders, toggles, and select lists (combo box and multiselect).`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "45.80357142857143%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAAA5ElEQVQoz4VSQQ6DIBD06X1JX9BDn9B7T42XPsAeJFGiAoIKU5cUghbbTSZkd8nMOFg457AvmgUc7jJzqiI0wzBASollWTYXtNbgnEMI8bWzzkJqFXs6PaG1Fk3ToK5rjOOIMKMiEZozxjBN01ZsMXiw50YoOpzn2SMQRRdrT0hjSB2RyzSA4l+Gv5AT8g4pn7blmFaHZD/nZi+Wq0ioRA+tethZ+wxT0q7roJSCMcYjkFG2ZVnGHcX1eWWH693hdAHON8Q0AmEgItCjBELqq6ryfwEhIQS4BF6NA+u2gf/K9+iT39sNxHIzLQpyAAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of selection controls",
        "title": "Example of selection controls",
        "src": "/static/b0dec25464d2d15c949e71ae19170e47/3cbba/Selection_controls.png",
        "srcSet": ["/static/b0dec25464d2d15c949e71ae19170e47/7fc1e/Selection_controls.png 288w", "/static/b0dec25464d2d15c949e71ae19170e47/a5df1/Selection_controls.png 576w", "/static/b0dec25464d2d15c949e71ae19170e47/3cbba/Selection_controls.png 1152w", "/static/b0dec25464d2d15c949e71ae19170e47/0b124/Selection_controls.png 1728w", "/static/b0dec25464d2d15c949e71ae19170e47/99571/Selection_controls.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <br />
    <p><strong parentName="p">{`Deciding what to use`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Control`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Context`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/checkbox/usage"
            }}>{`Checkbox`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To select or deselect one or more choices`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Agree to terms and conditions, add optional items, select all that apply`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/radio-button/usage"
            }}>{`Radio button`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To select only one option from two or more choices`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Pick type, shipping method, etc.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/toggle/usage"
            }}>{`Toggle`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To choose one of two or more binary options`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changing user settings; On/off; Show/hide`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/file-uploader/usage"
            }}>{`File uploader`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To upload/attach a file or multiple files to a form`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Attaching SSl certificates; adding config files to support tickets`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/combobox--default"
            }}>{`Combo box`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To select a single item (with typeahead functionality from a longer list`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Choosing a state, country, or language preference`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/multiselect--default"
            }}>{`Multiselect`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To select multiple items from a longer list`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Add a product example for MultiSelect`}</td>
        </tr>
      </tbody>
    </table>
    <h4>{`Radio buttons:`}</h4>
    <ul>
      <li parentName="ul">{`Pre-select a default option for the user; if the user selects a different option the default is deselected.`}</li>
      <li parentName="ul">{`For null options, provide a radio button with the label “None”.`}</li>
    </ul>
    <h4>{`Radio buttons and checkboxes:`}</h4>
    <ul>
      <li parentName="ul">{`Radio buttons and checkbox item text falls to the right of their controls.`}</li>
      <li parentName="ul">{`When possible, arrange the checkbox and radio button groups vertically for better scannability.`}</li>
    </ul>
    <h4>{`Toggles:`}</h4>
    <ul>
      <li parentName="ul">{`Always label toggles with the affected attribute due to accessibility constraints; color cannot be the only indicator.`}</li>
      <li parentName="ul">{`A standalone toggle or a checkbox can be used for a single option that a user can turn on or off.`}</li>
      <li parentName="ul">{`Toggles are very common controls in instantly updating forms, where submission is not required.`}</li>
    </ul>
    <h4>{`Select lists:`}</h4>
    <ul>
      <li parentName="ul">{`When you have more than five options to present to the user, use a select list (combo box or multiselect), not a checkbox or a radio button.`}</li>
    </ul>
    <h4>{`Bound entry controls`}</h4>
    <p>{`Bound entry controls allow users to input numeric data, like dates and times (e.g. number input, date picker, and slider components). They restrict user input and rely equally on keyboard and mouse interactions. They only allow valid entries, so field validation isn’t needed.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "45.80357142857143%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAAAqElEQVQoz52RAQvEIAiF+/+/tkZt1SqPT3B0Y7uNE0TR5yt9bowhV3ZX/2XMOJJSiqzrKq016b1rAyfftk32fde81vrVn3E4poQxRvHeKyk5BBgganjOWUII2jMSIp8wjBLOq9mL/658EF751Wpv3M2/uhPkPPQoSkpJlmVRccjtngjC7cyocUcw9IjMgEGwQxQOTdGaFmcgr4M7Y4i4CeneHP9p1Rn3AWi4x/e8fljaAAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of bound entry controls",
        "title": "Example of bound entry controls",
        "src": "/static/b3b365e6d063ec3ddf4b9e82d6b5ddbc/3cbba/Bound_entry_controls.png",
        "srcSet": ["/static/b3b365e6d063ec3ddf4b9e82d6b5ddbc/7fc1e/Bound_entry_controls.png 288w", "/static/b3b365e6d063ec3ddf4b9e82d6b5ddbc/a5df1/Bound_entry_controls.png 576w", "/static/b3b365e6d063ec3ddf4b9e82d6b5ddbc/3cbba/Bound_entry_controls.png 1152w", "/static/b3b365e6d063ec3ddf4b9e82d6b5ddbc/0b124/Bound_entry_controls.png 1728w", "/static/b3b365e6d063ec3ddf4b9e82d6b5ddbc/99571/Bound_entry_controls.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <br />
    <p><strong parentName="p">{`Deciding what to use`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Control`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Context`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/number-input/usage"
            }}>{`Number input`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To increase or decrease incremental values`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Order quantities`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/slider/usage"
            }}>{`Slider`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To select one number from a numerical range`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Percentages, volume, timelines, data visualization`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/date-picker/usage"
            }}>{`Date picker`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To input/select a single localized date or a date range from a calendar`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Scheduling trips, meetings, and events`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/timepicker--default"
            }}>{`Time picker`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To input time in hours/minutes`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Scheduling meetings and travel times`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Offering help`}</h3>
    <h4>{`Tooltips`}</h4>
    <p>{`Tooltips can be very useful for providing additional explanation to users that may be unfamiliar with a particular form field. They can also offer rationale for what may seem like an unusual request. However, `}<a parentName="p" {...{
        "href": "https://www.nngroup.com/articles/tooltip-guidelines/"
      }}>{`research suggests`}</a>{` that users should not have to dig around for a tooltip to access information that’s essential for the completion of their task.`}</p>
    <p>{`In Carbon, we use the “i” icon instead of the “?” icon because it indicates additional rather than essential information.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAABDElEQVQoz51TO4qEQBT0LB7CwEATMRRMPZCRgR7B0AOYeQq9gogo+Jvxi1rDa7aH3t2RndkHRXU/2qKK95TwVed5Qiy6vwvxe0m8EK/riuM48EmJZqSfjaqqkOc5+r7HMAzfcL/fMU0TxnFkoPOfDrdtu3RCzklknucnLgV5zCzL4HkefN9/IggCuK6LNE3fj7zvO2tEUQRFUWAYBjRNg6qq0HUdsiwjDEP2hsen2NzIL4dcMEkSWJYFx3Fg2zY7E5umiTiO2RuKvSwLG+BlZN5omoYNpes6tG3LmEB9EvhoysR1XaMsSxRFwZimTW5oABTxdruxuJwvI4t7yAV4LBG0BSJeRn71p/x3sR97VT2H9pKvtQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Tooltip appears on hover (desktop) and on click (tablet and mobile).",
            "title": "Tooltip appears on hover (desktop) and on click (tablet and mobile).",
            "src": "/static/f6a0298854b8dac6d66d1f227ddd97b9/3cbba/Tooltip.png",
            "srcSet": ["/static/f6a0298854b8dac6d66d1f227ddd97b9/7fc1e/Tooltip.png 288w", "/static/f6a0298854b8dac6d66d1f227ddd97b9/a5df1/Tooltip.png 576w", "/static/f6a0298854b8dac6d66d1f227ddd97b9/3cbba/Tooltip.png 1152w", "/static/f6a0298854b8dac6d66d1f227ddd97b9/392b1/Tooltip.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Tooltip appears on hover (desktop) and on click (tablet and mobile).
        </Caption>
      </Column>
    </Row>
    <h4>{`Do:`}</h4>
    <ul>
      <li parentName="ul">{`Use tooltips with the outlined “i” (info) icon.`}</li>
      <li parentName="ul">{`Use tooltips for explanatory or added information.`}</li>
      <li parentName="ul">{`Tooltips are microcontent; keep them concise.`}</li>
    </ul>
    <h4>{`Don’t:`}</h4>
    <ul>
      <li parentName="ul">{`Tooltips are not catchalls for content that doesn’t fit elsewhere; they must be used intentionally and very sparingly.`}</li>
      <li parentName="ul">{`Never house essential information in a tooltip.`}</li>
    </ul>
    <h4>{`Help text`}</h4>
    <p>{`Help text appears below the input label and assists the user to provide the right information. Help text is always available, even when the field is focused, that’s why it’s the correct choice for need-to-know information. For context or background information that is “nice to have,” use placeholder text or a tooltip.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAdklEQVQoz6WRSwrAIAxEvf89deXCH/5QpygVtNQS6MBDDDhOEoZbrTU81WsU1vdsvfSzlDKotYKqNcxm2I2UUtBawzmHEAJijNuH5ISzIKUE5xxCCBhjYK0d5idmJ8eWeyrv/SDnjJTSJ6+Gp6X8niF1q18zvAAMlSmcEHXCwAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Input field with help text",
            "title": "Input field with help text",
            "src": "/static/26917ab8b264c46654253645e25d89c9/3cbba/Help_text.png",
            "srcSet": ["/static/26917ab8b264c46654253645e25d89c9/7fc1e/Help_text.png 288w", "/static/26917ab8b264c46654253645e25d89c9/a5df1/Help_text.png 576w", "/static/26917ab8b264c46654253645e25d89c9/3cbba/Help_text.png 1152w", "/static/26917ab8b264c46654253645e25d89c9/392b1/Help_text.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Do:`}</h4>
    <ul>
      <li parentName="ul">{`Think of help text as crucial information that is secondary to the input label.`}</li>
      <li parentName="ul">{`Keep help text as short and specific as possible.`}</li>
      <li parentName="ul">{`Only use help text when truly necessary to avoid overloading the user.`}</li>
    </ul>
    <h4>{`Don’t:`}</h4>
    <ul>
      <li parentName="ul">{`Never use help text in place of field labels.`}</li>
      <li parentName="ul">{`Help text should not run longer than the input area.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAAAxElEQVQoz51SCwqFMAzz/vdUYX4G06FOXX0pZOyBH7QQO7subUoL+VmM8RS8e2PF3YOrQncNKOG+70IPbNsmXwwcBQ7jOErbttJ1nWIYBlmWRdZ1lRCCep7z/zwOrx3i472Xuq7FGCNN02gBAMQ5zmKAc045kuR5nrUjdkXpuTH2NPM/yX3fa0VUm6ZJZwmwqLU2jQOAVOawgUQIySAFGSVTIh6iQFVVirIsNR/zZg7uEyHlXIFr8ZT3uIef1+btAt8t9gHxoGIUlVXwrwAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of input field alignment",
            "title": "Example of input field alignment",
            "src": "/static/5d6da79f197b0d804e73137d3d71a983/3cbba/Help_text_alignment.png",
            "srcSet": ["/static/5d6da79f197b0d804e73137d3d71a983/7fc1e/Help_text_alignment.png 288w", "/static/5d6da79f197b0d804e73137d3d71a983/a5df1/Help_text_alignment.png 576w", "/static/5d6da79f197b0d804e73137d3d71a983/3cbba/Help_text_alignment.png 1152w", "/static/5d6da79f197b0d804e73137d3d71a983/392b1/Help_text_alignment.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <p>{`When fields appear side-by-side and one input has help text while the other one doesn’t; always top align the input fields, not the labels.`}</p>
    <h4>{`Placeholder text`}</h4>
    <p>{`Placeholder text provides hints or examples of what to enter (e.g. YYYY-MM-DD). Since placeholder text disappears once the user begins to input data, it should not contain crucial information. When the requested input may be unfamiliar to the user or formatting is in question, use placeholder text.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAZklEQVQoz6VR7QoAIQjr/d+1+lFhEbXr4gIJT+5jMGRDpqjBQO8dq34hzzC4sAyud+8OvM/sG6aUUGsVwx9vyE3vPay1cM7NGkKYQ2KMIltremApBTlnENHkqTWKgdIN30C94d8vHw7yKlZVUik7AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of placeholder text",
            "title": "Example of placeholder text",
            "src": "/static/4b95db4583e0e1eab33307494a7dd9f6/3cbba/Placeholder_text.png",
            "srcSet": ["/static/4b95db4583e0e1eab33307494a7dd9f6/7fc1e/Placeholder_text.png 288w", "/static/4b95db4583e0e1eab33307494a7dd9f6/a5df1/Placeholder_text.png 576w", "/static/4b95db4583e0e1eab33307494a7dd9f6/3cbba/Placeholder_text.png 1152w", "/static/4b95db4583e0e1eab33307494a7dd9f6/392b1/Placeholder_text.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Do:`}</h4>
    <ul>
      <li parentName="ul">{`Keep hints as short as possible and never overrun the input field.`}</li>
      <li parentName="ul">{`Properly anonymize examples rather than using real values.`}</li>
    </ul>
    <h4>{`Don’t:`}</h4>
    <ul>
      <li parentName="ul">{`Use placeholder text to communicate complex and lengthy requirements like password requirements. Instead, use an infotip.`}</li>
      <li parentName="ul">{`Provide placeholder text when it isn’t necessary.`}</li>
      <li parentName="ul">{`Ever use placeholder text as a replacement for field labels.`}</li>
    </ul>
    <h3>{`Buttons`}</h3>
    <p>{`Use a primary button for the main action, a secondary button for secondary actions like Cancel or Discard.`}</p>
    <h4>{`Button alignment`}</h4>
    <p>{`Alignment refers to whether the buttons are aligned to the right or the left of the container or layout. Button alignment depends on the type of form that you are building. We’ll touch on alignment briefly here as it relates to the button component and offer more detailed information about `}<a parentName="p" {...{
        "href": "#variants"
      }}>{`form variations`}</a>{` below.`}</p>
    <h4>{`Margins vs. full bleed`}</h4>
    <p>{`In side panels, dialogs, and any other forms within tiles, the button group should span the width of the container and buttons should bleed to the bottom edge. If the button content is too long for this arrangement, stack buttons vertically (with primary button on the bottom) and maintain their margin and padding. See `}<a parentName="p" {...{
        "href": "/components/button/usage"
      }}>{`button usage guidance`}</a>{` for more information.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Alignment`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Bleed`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Use case`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Left-aligned`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Non-dialog, in-page forms`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Right-aligned`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Multi-step forms/wizards when the primary action implies a navigation step forward`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Full-width`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`All forms that are presented in dialogs and side panels and in some cases, forms within a tile`}</td>
        </tr>
      </tbody>
    </table>
    <h4>{`Button emphasis`}</h4>
    <p>{`Emphasis refers to the position of the primary button in relation to secondary and tertiary actions. When using multiple buttons, the primary button appears to the right and any secondary buttons appear to the left. `}<a parentName="p" {...{
        "href": "https://www.nngroup.com/articles/ok-cancel-or-cancel-ok/"
      }}>{`Research`}</a>{` has shown that performance differences between secondary and primary button placement are negligible, however maintaining consistency throughout a product, offering, or platform is crucial. The Secondary / Primary button order (secondary to the left and primary to the right) is therefore our required guidance and should be followed at all times.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "63.04347826086957%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAB50lEQVQ4y41S24oaQRD1h7L4KPjgmx/ki28J+RTfQla8QGCCjkoIYXRNnGQIwSWI7IJuIOi4c++5eLar3OnMSpakoGi6+vSpU5dSkiTwfU+6D8/znngURSA7Ho/sQRD8FUdxeicrbTYbXF620e120ev12Pv9PtrtNkzTVIRCCGjae8YWcZ1OB4PBgN+ZME5S7A+uUhSGIWckz0G5wnvHx+HgIJCYIpZOpTBLE/lDII5jJiCg67rsxZKzLHv8lCJLIsZS+YSjUxE6fgrdEvB8ynrKSGqLhERG9t0yMfmyxbebFEkcwH3EPSFM0gy2K2TGWKrMz5OnaaoUknnys+1IouCEzXE0WEWIfxip+1Pu85b3ubTb7WAYBubzq4LPMZ1OsV6vFZiULBYLjn+W7zluNpvBsixWyQo1TcPFxQu8eduFpn+CNphgPB7zKiyXS6WAElerVbx89RqjD3O80yYYjcYYDocwZBJFqOs6arUa7u5+PVsymW3bqNfrvHccP2uB6iEpLJfLWK1WHKSdEiKSzZfr40cydiLc7/eoVCpotVp8p21wHUfusIeDHGqOK1FfGo0GttstyyZgLEIY1wK3v2PKzUBaj2azye0gC8MIP69/4KN5i683REaD+48pEygv+7y8cxwlfwDff8ePVwRX/gAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of dedicated page forms",
            "title": "Example of dedicated page forms",
            "src": "/static/68d4ce39f4bcdbeada55867e0f338b39/3cbba/dedicated_pg_buttons.png",
            "srcSet": ["/static/68d4ce39f4bcdbeada55867e0f338b39/7fc1e/dedicated_pg_buttons.png 288w", "/static/68d4ce39f4bcdbeada55867e0f338b39/a5df1/dedicated_pg_buttons.png 576w", "/static/68d4ce39f4bcdbeada55867e0f338b39/3cbba/dedicated_pg_buttons.png 1152w", "/static/68d4ce39f4bcdbeada55867e0f338b39/392b1/dedicated_pg_buttons.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of dedicated page forms</Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "63.04347826086957%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACLElEQVQ4y41TW28SURDmf6sv3BcEAqXcghFCGywsu2ChWGPwwZjYLZdUlFuVWKDLgw/CcpPrJ3NoT+VFO8mXzcl8+82cb+boNpsNlsslx2q1wmKxwHQ6xXw+BwVxCJT7m0uYzWYMlKfQ1et16PV6mM1mBqPRCJfLhUgkgmKxiPsYjUZwu92cSzyr1YpwOIx8Ps+aYIK1Wg0GgwEWi4WBfohGo+j1ephMprwyCXo8HiZEPJPJBIfDgVqtDk0b8Vvoms0mbDYb64ogCFakUtKurc2cC47HYwSDQdjtdsYjMa/Xi+Gvn9vsCuv1etchCQqCwAgEm82OF+Eg3n34gvrNklS5YCAQYMWdTicT9vsPcfjyLeQ3V9xr3WQyQafTQbfbZej1btH+3sLHYhvfVEbjA1FVdY+rqrdodSb40f/NC+vwn1ivN/w6/wru4WAwQKlUYhNVFAUXFxfsqyiXuOl0OZmmSBtRqVRQKZdQLpcZ6Ny6vn7wkEi0BrFYDIlEAslkEmIqxXzN5XK8g+FQY94FgmHETyTE4ycQRRE+n495y9em0WiwddA0be8Kx0dHe4K7tXHj6vNXdl7duXCpfEIoFNp6vHzokPaQltRiEfDUo+CJv41nhgOc5V5zQSr4fDvZ7Nk5G9xpRoQQKMDgOUcocID54k6w3+8jm81ClmVIkgRJzkBOnyKdzqBarXJBeor0IiRJ3ubSzJ7juIhXCRnvCwX2DB815fvpPSaI9wdC7okSYc9VUQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Dialog and side-panel forms",
            "title": "Dialog and side-panel forms",
            "src": "/static/b7dae2cf7f6e50cc9a4e1a7f273f3329/3cbba/dialog_and_panel_buttons.png",
            "srcSet": ["/static/b7dae2cf7f6e50cc9a4e1a7f273f3329/7fc1e/dialog_and_panel_buttons.png 288w", "/static/b7dae2cf7f6e50cc9a4e1a7f273f3329/a5df1/dialog_and_panel_buttons.png 576w", "/static/b7dae2cf7f6e50cc9a4e1a7f273f3329/3cbba/dialog_and_panel_buttons.png 1152w", "/static/b7dae2cf7f6e50cc9a4e1a7f273f3329/392b1/dialog_and_panel_buttons.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Dialog and side-panel forms</Caption>
      </Column>
    </Row>
    <h4>{`Do not top-align buttons`}</h4>
    <p>{`There is a trend among product teams to pin buttons at the top of a dedicated-page form. We want to discourage this arrangement for several reasons.`}</p>
    <p>{`First, we should only be asking the user for essential input and we should elicit that information in a concise, deliberate way. So we should assume that the user scrolls through the appropriate inputs before submitting a form.`}</p>
    <p>{`Second, the dedicated page form is not a modal and does not prevent the user from accessing their previous workflow. A back button will be available to them as part of the breadcrumb at the top of the page, or via the progress indicator component (if your form is part of a multi-step flow). The browser back button is also available. In short, back should never be an action on a secondary button. The secondary button is usually reserved for cancelling the task.`}</p>
    <p>{`Third, and most important, the top-pinned buttons create a very awkward relationship with the content when the user finishes the form and is ready to submit. If we feel it’s necessary to pursue pinned actions in the future, we should look into a pinned footer or tray to contain button groups.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
        <DoDontExample type="do" caption="Do arrange primary and secondary buttons at the bottom of the form." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABvklEQVQ4y62UXU/CMBiF+aeoVwSiv8pbSYzJjCYEvFJvcITEGBUlASYfA2TABvs67lTfOYSBiXY56dq9fd7Trm0G/1wyYRiqF9Z/kTAy8vLXIowVYFrW37iLHfq+rxqapuFgfw9HR4coFArI5XI7lc/nkc1mUSwWFSMIAmSC4DNDu2Pg+KSM88sKripl3N3pqNfrqNVq0HVd1SK2pa9arcIwjG+HYtl2XHTeTIxGY1iTCWzbxnQ6xWKxgOd5W0Vna8DB0Iqy1tButTAev6Pf76PZbEYJRgo+m80wn8+VmMiyrLhm0jWg49gKQBAHO46jMtOB67orEveMY71cLjcBHfR6PUyi6TIoDcg2v+0EspMBMjXTNNFoNDAcDuMpb9JWoKyLZCeYbrhGSck6yhqmAhlINxRBlEwzKUIljmM2ArnBk7ueg9QfJtRbXUM5DFLYFx+9NGDgBwijTc868HxV+66vXCcd/RybCuzOBnjoPuNp8Ion8xWPvRc039vwoz8vG1nKRqBMRYJ14x6nt+fQ9BK0eglnNxeoNK4R8qHzL7BsrTUgPxAoim+bpKK+ZIxo5ehtvQ/DH/rNffjfN/YHoy//5P45aFwAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do arrange primary and secondary buttons at the bottom of the form",
              "title": "Do arrange primary and secondary buttons at the bottom of the form",
              "src": "/static/835e0257f0533981915164231ee42c49/377f4/do_bottom_buttons.png",
              "srcSet": ["/static/835e0257f0533981915164231ee42c49/7fc1e/do_bottom_buttons.png 288w", "/static/835e0257f0533981915164231ee42c49/a5df1/do_bottom_buttons.png 576w", "/static/835e0257f0533981915164231ee42c49/377f4/do_bottom_buttons.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span>
        </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
        <DoDontExample type="dont" caption="Do not top align primary and secondary action buttons in your layouts." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAB+UlEQVQ4y7WU32oTQRTG90EFa0GbkFolRhHxVp/AR/BW0ZsIVkhaoYoozbq9UDAQaKskJtnubHY3+/frfBOnHZuJN60DH2d255zfOWd2Zh1c8XCqqlIT2stIMxw9uezQjL+Aq7Ka86IspQqpCnkJpcVypaxTFIVybrfbWL+xhq2t22g0GqjVakuq1+toNZt4cK+F1t1NPG5u4M6ta3j96qVilDKZU5aL7EfHP/H8RQdv3nax0+1gf78Hz/Pgui56vR6+Hhzgc2cHT+ubeLR2HU/uP8P6w0+42dzGd/fHH2B1/lHiJMPxrzGmUx8iCBDHMcIwxHw+R57nyGUnaSDgdXfx5d02vA/f0PkY4f3eFPFJcv5RNPD3RMhKXBwdHsL3TzAajTAYDGSCqYLPZjNE0pasRCorUlRFiDKfIc3SZWCSxApAEIOTJFF7wuqyLFsoTZHKiiO5LmS1QoQIpGUXFmCC4XAoHQLVqhUoxWeu0YeJadPUUiFf0kG1FkUYj8fo9/uYTCZnLdv0T6AQQjno7ASzGrZkiu+1H2NWAunIaiiCKN2mKUK1H2OsQB5w8woyyPd9BdX7qK2+DHrw/dnVuwjUzwxkZupiy1wzr6xZzMoKGWTbPxOohxXIsrnAo0JxrvfMto/aTx+tJaCGmDKDTNn8/t//8Kr/2KfUev15d7fVwwAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not top align primary and secondary action buttons in your layouts",
              "title": "Do not top align primary and secondary action buttons in your layouts",
              "src": "/static/39fc1ed6f1f3a8c616c6c94227b1942c/377f4/do_not_top_align_buttons.png",
              "srcSet": ["/static/39fc1ed6f1f3a8c616c6c94227b1942c/7fc1e/do_not_top_align_buttons.png 288w", "/static/39fc1ed6f1f3a8c616c6c94227b1942c/a5df1/do_not_top_align_buttons.png 576w", "/static/39fc1ed6f1f3a8c616c6c94227b1942c/377f4/do_not_top_align_buttons.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span>
        </DoDontExample>
      </Column>
    </Row>
    <h4>{`Naming actions`}</h4>
    <p>{`Abstract terms like “Submit” give the user the impression that the form is generic. Although brevity is key in buttons, try to tell the user specifically what action your button will perform.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
        <DoDontExample type="do" caption="Do use task-specific language in your buttons." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAA/klEQVQ4y+2U3UoCQRTH14sQC/LGV+rOm8wgUwLTEHyTNPrQqKDXMNH86CUiegDtIcrZX+dwdkW2vdnqwgsP/JjZmZ3//M/MYTz+ObyN4JoK+r59OGm/FslwwVrVUJYOQ9HfhPMjDsOB9w+4HkD3GTpCd2Ro/3YM9xO4C3iYwqX8+zYPRN2KQ7Wu8fgiA8ew24DtOmxVIX0KmRqkTmTuSCgFVIS8CWt8LmIEdWevCLkmZBsmtHNmG3hlYV84EArCobAnzkcxgmHKar/1BBd9aPetDftXA0v9ZmjoUZz34HUWk3L0YP98KcuycWY9CbrmR9n4kVpKyqrG5nFYQ8Fvljj9ggSo2S8AAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do use task-specific language in your buttons",
              "title": "Do use task-specific language in your buttons",
              "src": "/static/c607860df69bfc628e24791b37eb5d5f/377f4/Content_do.png",
              "srcSet": ["/static/c607860df69bfc628e24791b37eb5d5f/7fc1e/Content_do.png 288w", "/static/c607860df69bfc628e24791b37eb5d5f/a5df1/Content_do.png 576w", "/static/c607860df69bfc628e24791b37eb5d5f/377f4/Content_do.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span>
        </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
        <DoDontExample type="dont" caption="Do not use vague language to describe an action." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAA3ElEQVQ4y+2U3woBQRSHx60b5Z3cuUGKUm6UPIko/4XyGMgi8gxKuScPwc78zHF2tXbnZnHhYk99nebUfP1mdlqBH5eIhH8qVIoXUve7HQ7p7CUH8UroSj8pqXwJ3cHpCnRXwGAD9NfAUPfxFhgZmOyAtgUcL45UehJSdKrpXg8KQKIKxCtArMxrUTRQ0qRZTnWzDUJKI7JAssY8N2U0OZ6/kdek+CQBoXtkit+YA80F99aSr6BjBelpUX0GHM6GI/sv9uuP4n02FD0MlCzwbJTvLYXF64h+Dn8ofADr3Pz1ftFRsgAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not use vague language to describe an action",
              "title": "Do not use vague language to describe an action",
              "src": "/static/0ea45cea1d854b04e39507a5cb857e7f/377f4/Content_do_not.png",
              "srcSet": ["/static/0ea45cea1d854b04e39507a5cb857e7f/7fc1e/Content_do_not.png 288w", "/static/0ea45cea1d854b04e39507a5cb857e7f/a5df1/Content_do_not.png 576w", "/static/0ea45cea1d854b04e39507a5cb857e7f/377f4/Content_do_not.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span>
        </DoDontExample>
      </Column>
    </Row>
    <h2>{`Behavior`}</h2>
    <h3>{`Errors and validation`}</h3>
    <p>{`Effective and immediate error messaging can help the user to understand the problem and how to fix it. First, inform the user what has happened, then provide guidance on next steps or possible resolutions. Always present error states on the form, and use inline errors whenever possible.`}</p>
    <h4>{`Client-side validation`}</h4>
    <p>{`We recommend validating the user’s data before form submission. This type of real-time, inline validation (a.k.a. client-side validation) should happen as soon as the field loses focus. This will help to easily identify the elements that need to be corrected.`}</p>
    <p>{`The validation label below the field should be as informative as possible when describing the issue with the user’s data. For example, if password limitations require 16 characters, but the user inputs a password with only six characters, the text should read something like, “Password must be at least 16 characters.”`}</p>
    <p>{`Common user errors include:`}</p>
    <ul>
      <li parentName="ul">{`Incorrectly formatting data`}</li>
      <li parentName="ul">{`Leaving a mandatory field blank`}</li>
      <li parentName="ul">{`Leaving a mandatory field incomplete`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAkUlEQVQoz52RawrCMBCEc/+ziQfQH1ZF02AIUhPIe7otpoRioHbgI8uSXYZZBlLOGeXdQ72D4avS2KN6ltWNlBLMMMwk74EYkUNoQ/9/OlwWOgd5OkNcOsjuCnW748M50a/g0ELgfTgiat1emMmRfTxhewFLg1PtXhJBG0KvMPBKzU63Z7gx12aGy+WmuvDnlUcB7ydyODbScAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of client-side error message",
            "title": "Example of client-side error message",
            "src": "/static/07e6afea68ae82958dc9466afe287ecf/3cbba/Inline_error_message.png",
            "srcSet": ["/static/07e6afea68ae82958dc9466afe287ecf/7fc1e/Inline_error_message.png 288w", "/static/07e6afea68ae82958dc9466afe287ecf/a5df1/Inline_error_message.png 576w", "/static/07e6afea68ae82958dc9466afe287ecf/3cbba/Inline_error_message.png 1152w", "/static/07e6afea68ae82958dc9466afe287ecf/392b1/Inline_error_message.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Server-side validation`}</h4>
    <p>{`Inline notifications come into play when server-side errors are involved, i.e. the user tries to submit a form in its entirety and the page is reloaded with the detected errors.`}</p>
    <p>{`In these situations, use an inline notification as well as inline error messaging wherever possible to help users make the fix. Inline error messages should disappear when the form criteria is met.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <img {...{
          "src": "/d7392a9fd7cda3e87fb9e4a9f8193c7a/server_side.gif",
          "alt": "Example of server-side"
        }}></img>
      </Column>
    </Row>
    <h3>{`Enabling and disabling buttons`}</h3>
    <ul>
      <li parentName="ul">{`For short forms that require server-side submission before returning errors, we recommend disabling primary action buttons until all of the form’s requirements are met.`}</li>
      <li parentName="ul">{`For longer forms, do not disable primary action buttons because the error messages and the primary action button may not be visible on the screen simultaneously.`}</li>
      <li parentName="ul">{`When a user submits a form, disable the primary action button to prevent duplicate submissions.`}</li>
      <li parentName="ul">{`If it’s going to take a while to process a form, communicate this to the user with feedback messages and progress indicators (e.g. spinners or progress bars).`}</li>
    </ul>
    <h3>{`In-line editing`}</h3>
    <p>{`In-line editing enables users to edit form text in situ instead of taking users to another page to edit their entry. This saves users from having to refresh the whole form in order to make an edit.`}</p>
    <p>{`Carbon does not have consolidated guidance around inline editing. Since it’s something a lot of products approach in different ways, we’d like to offer more robust, centralized guidance in the future.`}</p>
    <h3>{`Designing for longer forms`}</h3>
    <p>{`Product designers often ask about the appropriate length for web forms. Unfortunately, there’s no one-size-fits-all answer. Your audience and their intentions, along with the context of your product will determine the solution that’s best for you. Here are several techniques to help make longer forms less overwhelming.`}</p>
    <h4>{`Progressive disclosure`}</h4>
    <p>{`Use progressive disclosure to reveal any additional content that may arise based on the user’s previous selection. This kind of show/hide approach allows the user to focus on relevant information while keeping workflows short.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "82.20108695652173%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAQCAYAAAAWGF8bAAAACXBIWXMAAAsSAAALEgHS3X78AAABHUlEQVQ4y61Ti26EMAzj/z8U8TxE6egz9UhvQQPGrjetyAqCYuzGqbCtlBJerZI9vCrZOM8z2rZF13Vov8D3dV3DGruT3kHe74TrukIpBWstvPPwhquD9x70hoOdcJomNE2DEMJzg3ag2QAuItlwhQsgZRF7netFodY62x4fD+hF54/IeiRK94iEFCjXnfAsm4gyUNiEH5vyn6jOnbo78GJC+YjPj2PS932ODzeIa46NtcVZPOSQY8MxiTHu4K5/z9lBtVxny9LlcRwzKRO9a/vQZXlgjMEwDNk2k7PqZVkOUBvMxwpwrLac0vCM2CWHbJVVimWuDLZ8vhcH6dWksDpW5baRKxq132ZZ/s4zLYQlZybEIrm6i0P646R8ArvQ7u4hwFFIAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of progressive disclosure",
            "title": "Example of progressive disclosure",
            "src": "/static/9e953590c9e1a645629dbab6da8b9119/3cbba/progressive_disclosure.png",
            "srcSet": ["/static/9e953590c9e1a645629dbab6da8b9119/7fc1e/progressive_disclosure.png 288w", "/static/9e953590c9e1a645629dbab6da8b9119/a5df1/progressive_disclosure.png 576w", "/static/9e953590c9e1a645629dbab6da8b9119/3cbba/progressive_disclosure.png 1152w", "/static/9e953590c9e1a645629dbab6da8b9119/392b1/progressive_disclosure.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Accordion forms`}</h4>
    <p>{`Accordion forms allow users to dynamically expose and hide sections of related information. Like progressive disclosure, accordion forms allow users to focus on relevant information without having to navigate between pages. As a general rule, this technique should not be used in dialog forms.`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.lukew.com/ff/entry.asp?1190"
      }}>{`Research suggests`}</a>{` that accordion forms can greatly enhance completion speed and page load times. However the same research also suggests that confusion can arise for users when it comes to primary action buttons and whether they apply only to sections vs. the full form.`}</p>
    <p>{`The IoT team has done some design explorations around accordion forms but more design iteration and user testing is needed before Carbon solidifies our guidance around this interaction. Keep an eye out for refined usage examples in the future.`}</p>
    <h4>{`Multi-step forms`}</h4>
    <p>{`A multi-step form spreads form fields across multiple screens and incorporates a `}<a parentName="p" {...{
        "href": "/components/progress-indicator/usage"
      }}>{`progress indicator`}</a>{` (vertical or horizontal) to track a user’s status step by step. There should be a logical relationship between the fields on each screen and a linear relationship between sections.`}</p>
    <p>{`This approach is good for saving form progress along the journey and allows users to return to a previous step to review their submissions.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "66.57608695652173%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABjElEQVQ4y62Ty07CQBiFeQzgAYAAC4GdMSYaXxPfgos7dYdGBBZQREEoVLmUUnqZcuux80MJAgFinOTrP5N2zpw5nfHYto3/bB7+mM1mGAwGRL/fpzocDv8ECQpCBaFQCIFAALHYGYLBALxeL/x+P+Hz+db9Y5Bg9bOL84tL3CaTuH94RDZ7h0wm49TsmnQ6g1Qq5dT0QZaCDQlX1zeOUwE6A2RFw3RqYWItsSwG216cnmGlUkY8Hkcu94TOVw/CWx3v9RZqH02iWmugLXWhqioxGo2I7T5nJSggkYjj+eUV37KFhtiFojKUmww1J46erGGgGBDbbbRaLWiaBlmWIYoi/Qg+dlkJVhCNRpHP58k2Mw1nqwyGYcA0dBpbzKQJiqLANE2C3q/6LmvBSCSCQqHgZDddTmJsnQs/qace1x1BvgrPhG0I7mv8Quzjl2CxWKSPJ5MJOeV1Pp/Twd/EvV0HBcPhMEqlEjnrdDqQJIkC13WdsuOVMx6PaRFX8OiWXWeum8ViscOmw+32A1DkmsFKqVfVAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "A multi-step form with a horizontally oriented progress indicator.",
            "title": "A multi-step form with a horizontally oriented progress indicator.",
            "src": "/static/50910470a123f3c794b69ed28a8bdfa1/3cbba/horiztonal_prog_indicator.png",
            "srcSet": ["/static/50910470a123f3c794b69ed28a8bdfa1/7fc1e/horiztonal_prog_indicator.png 288w", "/static/50910470a123f3c794b69ed28a8bdfa1/a5df1/horiztonal_prog_indicator.png 576w", "/static/50910470a123f3c794b69ed28a8bdfa1/3cbba/horiztonal_prog_indicator.png 1152w", "/static/50910470a123f3c794b69ed28a8bdfa1/392b1/horiztonal_prog_indicator.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  A multi-step form with a horizontally oriented progress indicator.
        </Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "66.57608695652173%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABbUlEQVQ4y62S3W6CQBCFfQx9AWOCJlrv2qRp0/esz2For9o0rQa5KzSpbeMFggoRQQQRTpkxa8D+/2xyGAK738yc2VKapvjPVaJHHMeYzWas6XTK0XGcX4mBuq6hVquhWq2i2WxyLJfLqFQqPxYDH14sHB4do9M5x9X1DeSLS8iyXFC32/2WtsBnAyenZxgOn0COBmH6Nw917R6tVguqqmK59DEej9lHy7JgmiZ7kyQJaIAUN5vNh2Kgputotw+gKCqCCLAzWBAEiKIIq9WKNy4WC0wmE9i2zVBalGBfW6CmodGo47anwHTi7KAF13XheR6DwjBksIB/2TIB63UJd/0BHs0ka9NikO/7DBXA+Xy+S0LV50V76PoVgL2+AtcLMg8NrjAPpEN0R8U9pX95iU52QEmSMg+VzLslRqMRV5MH0vt6vS5492nLAkgHDaNYIVVHk6bvNHkSJXlvMG+AYqIE2q+QpixapiQCmF+v9rqa5ygqevkAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "A multi-step form with a vertically oriented progress indicator.",
            "title": "A multi-step form with a vertically oriented progress indicator.",
            "src": "/static/5161e0eadc24a8fb0ca669ade3104991/3cbba/vertical_prog_indicator.png",
            "srcSet": ["/static/5161e0eadc24a8fb0ca669ade3104991/7fc1e/vertical_prog_indicator.png 288w", "/static/5161e0eadc24a8fb0ca669ade3104991/a5df1/vertical_prog_indicator.png 576w", "/static/5161e0eadc24a8fb0ca669ade3104991/3cbba/vertical_prog_indicator.png 1152w", "/static/5161e0eadc24a8fb0ca669ade3104991/392b1/vertical_prog_indicator.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  A multi-step form with a vertically oriented progress indicator.
        </Caption>
      </Column>
    </Row>
    <h2>{`Designing a form`}</h2>
    <h3>{`Layout`}</h3>
    <h4>{`Form headings`}</h4>
    <p>{`Headings describe the form. The heading should be the largest type size in the form hierarchy. IBM Product UI often uses the \$productive-heading-03 token for this purpose if the form is within a container or a dialog. A larger type size should be used if the form is the only element on the page. The title can also be followed by a short descriptor.`}</p>
    <h4>{`Group and section headings`}</h4>
    <p>{`Group headings describe a group of controls and fields within a form. Their size should also be adjusted depending on context and form heading size (i.e. the chosen token should be larger than the field labels but obviously smaller than the form heading). Inputs should be grouped to help users understand what is required of them in a logical way. Try to make the group heading short and precise, but, you can add a short description of the group if necessary.`}</p>
    <h3>{`Spacing`}</h3>
    <p>{`Users will be confused if inputs are too close together. To ensure sufficient spacing between single form elements as well as groups of inputs, use margins, spacers, gutters, and key alignments to guide you. See the `}<a parentName="p" {...{
        "href": "/guidelines/2x-grid"
      }}>{`2x Grid`}</a>{` for more information.`}</p>
    <h4>{`Form context`}</h4>
    <p>{`Forms can appear as dedicated pages or within dialogs, tiles, or side panels. The form’s context affects its layout and vertical spacing. As a general rule dedicated-page forms can handle more complexity. See `}<a parentName="p" {...{
        "href": "#variants"
      }}>{`form variants`}</a>{` below for more detailed usage guidance.`}</p>
    <p>{`On dedicated page forms, use the responsive grid to drive layout decisions. Dialogs and side panel forms will revert to a box model so designers will use mini units to guide field widths. Consistency of alignments and geometries in either scenario is key.`}</p>
    <p>{`Individual input fields default to a 40px height in product regardless of context. On dedicated-page forms, we recommend a 32px spacer between input fields. In contained forms, such as side panels or modals, designers can revert to 24px or even 16px between inputs.`}</p>
    <h4>{`Separating inputs, actions and sections`}</h4>
    <p>{`Vertical spacing between form sections also depends on whether the form is a dedicated page or a container. Spacing between groups should be adjusted in relationship to spacing between individual items. For instance, if vertical spacing between individual inputs is 24px consider a 32px spacer before the first input and between sections. If the former number is 32px, consider 40px for the latter.`}</p>
    <p>{`As a general rule, we recommend a 48px spacer between the last input and the button or button group. Again, this will vary in mobile and in certain contained forms.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "102.17391304347827%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAACBElEQVQ4y42UTa7TMBDHexDOwAZOxIoDIMEFWCOBxCXYsEIcgS0L3uN9IHao7Wu+bMdOYs/8GSdNmqRJVVe/Tp2k/8yMZ2YDWcwcDbz3KMsSVVXBWgtjTLuP1/vFWFh8urqJYr2g0Qa/b2/x5/ERD/cPuL+7w82vG+y3O+RZDlvaVpFJvnp6saPd9MqUWZCqBAfOHSi3AyETL1PTPgN5hgsHFJUg1tNUcPBQ3ta4GnmSQok3pTKorYM1JajxnTeBwALGLHnYh1zXNZ4OB+z2OxRFgUr249zxWh7XchgFttutiD6J6B7WuYnYGlGQJ4Lx4zwa45DvE9hMgSsJ8xqIB9FTyBFTw5uqFYNtAHkBXCMvalq7BMtzTIRx2iYhBwpI0hSHJGmt0hqmNNBmRtkR74UQpoK9h7EkgpRMncnpJhp1qtHI70ZKprMzcmmARHUVcOYhugINtYfKC+gjsYziH4JAI/q9lUbwc8HxJkg+0mwUslISoobW65yFPMmh3Iz9O6Avsyg49/DsAC6y5iG61os5NIW6Gp0VwyTi+XAISQmWU4aSASAn2KKEQno5XSH+p1nw8Fib+PmX8PwN4cVbwst3hGevCa8+U1tY5Ecjaza+zjqFuOvKfxnw4Rvj43fGJ+H9V8aXHzz0/9W9fHJ5aR4fq+ASa+MrWh+mBDrd48X5P13/AX5RF74UnQPfAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of form spacing",
            "title": "Example of form spacing",
            "src": "/static/361e70c214c6a040d0cc73d8240dd53e/3cbba/form_spacing.png",
            "srcSet": ["/static/361e70c214c6a040d0cc73d8240dd53e/7fc1e/form_spacing.png 288w", "/static/361e70c214c6a040d0cc73d8240dd53e/a5df1/form_spacing.png 576w", "/static/361e70c214c6a040d0cc73d8240dd53e/3cbba/form_spacing.png 1152w", "/static/361e70c214c6a040d0cc73d8240dd53e/392b1/form_spacing.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Rules`}</h4>
    <p>{`Designers often use rules to separate groups of information within forms. Carbon does not have consolidated guidance around rules within forms (i.e. width, thickness, vertical margins). We intend to provide more detailed guidance around their use in the future.`}</p>
    <h3>{`Columns`}</h3>
    <p>{`Based on research from the `}<a parentName="p" {...{
        "href": "https://www.nngroup.com/articles/web-form-design/"
      }}>{`Nielsen Norman Group`}</a>{`, Carbon generally recommends single-column forms, simply because multi-column forms are more prone to misinterpretation. However, when faced with larger screen sizes and a lot of empty space, multi-column forms may seem like a good idea. And in certain situations they are appropriate.`}</p>
    <p>{`If you would like to create a multi-column form, the number of columns should depend on the number of input controls on the page, their relationship to one another, and the screen size of the product window.`}</p>
    <p>{`Always use common sense to group related fields horizontally. Two to three inputs on a single line will not cause problems if they logically belong together. Here are some examples:`}</p>
    <ul>
      <li parentName="ul">{`[first name][mi]`}{` `}{`[last name]`}</li>
      <li parentName="ul">{`[credit card number][expiration date]`}{` `}{`[security code]`}</li>
      <li parentName="ul">{`[city][state/province]`}{` `}{`[zip code]`}</li>
    </ul>
    <p>{`Avoid overloading users with too much information, when a multi-step form may be a better choice.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
        <DoDontExample type="do" caption="Do consider multi-step forms when faced with a lot of inputs." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAB9klEQVQ4y82Uu24aQRSGeRUKChC3GlqeABoa8CZ+mJRORYFAXBwJQREpXZpIUYpIlixZiuK4iPE6sWUc24sFO7szy17+zBm0GGJMrNhFRvp0htmZT2c5syeCZx6RZxcGQaAmFJ9C6IiEk6eO0LGSoe/7Ctd1IYQDx3kcnufdZUiHaQwGAxQKBRSLRZTLZWy/1PBC07C1paFSraJSmVOt3qHJ56VSCf1+XzkomYWwVqshGo0iFothZ+c19r+e4fPeAY6+fYF+cowfpzpOJbq+ynA4hGEY9zNsNpvIZDLIZrPY3X2DCyPA4fdfOBreQL/guDRc3NzasCwGxiyYpikjk78thI4VYaPRQDKZRDqdRqvVhiU8XF1d43ps4+clw2RiykxuZZxgOp1iPB6rOSGEWC9MpVJK2G5LITNxfn4GwW04wga3bdhLcM4VlCEVZqOw0+motfkhoaAs6DBFWicpzSk+WhhuXgcXxEzuEQ8LSUaFCYX0bN1X4ft0d31S0C51Z+8J6/U64vE4EomELEpLrdEfTpVcxjQZZpzh06GN+geBt3v0FmuK0u12kcvlkM/n0ev1Ngpdh+H9Acerdw7aHzlms6UMw0+P7tVoNFLQwfDm/0kQ+PAWrzxn0Qs2NYe/NQxPulxvHhfd6qHm8C8tbaV9/dcd+zcTq8cVkGVk2gAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do consider multi-step forms when faced with",
              "title": "Do consider multi-step forms when faced with",
              "src": "/static/9edc4ddea44b8ef74ed06deac5d24def/377f4/do_multi_step_form.png",
              "srcSet": ["/static/9edc4ddea44b8ef74ed06deac5d24def/7fc1e/do_multi_step_form.png 288w", "/static/9edc4ddea44b8ef74ed06deac5d24def/a5df1/do_multi_step_form.png 576w", "/static/9edc4ddea44b8ef74ed06deac5d24def/377f4/do_multi_step_form.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span>
        </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
        <DoDontExample type="dont" caption="Do not overload the user with too many input controls at once, especially in modals." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAB+ElEQVQ4y82UTWsaURSG/VVd9OMX2FoX/QGlJdm57kJt/KiiuFCZQCEaN20lIERpd8KUVp1R0CAJaApNQFRwkGItOs6MX2/nXDrjIg6hxEIvvHPuvXPPc8+ce+basONm2zlwvV6zDtm7yGDYjM5dm8EwI5zP55hMJphOp38l8tE0bRPhcrlkA1EUEQgEEIvFEIlETEWjUUvRWvKpVCqMsVqtNkBBEBAMBpFMJsFxnCkaJxKJGzLmQ6EQC+YGsFqtwuv1olgsot1uo9FooNlsotPpoN/vo9frodvtmlaSJPA8D7/fz3y3Aj0eD8rlMgaDAYbDIcvRbDaDoihQVZXlmSzNkR99qs/nux1IL8iJkm0ARqMR22g8HkOWZQantbcCS6USG5PT5jRlfL+61iOqotX6htHPX5Bnmp530RpYq9XgdruZpd2NUvhTZVCmQ/yQrqBOJGAx1ucUnNUFHBxYAI2yyWQyyGazyOVyKBQKyOdPcZr/iCyv4KQMvP8MvOOBD1+Bo5MzhII+iNuAlI9wOAyXywW73Q6n0wmHw6HrMZ48fYZHL77g/l4LD/bO8XD/AvdeXuL5q08Iv3mtp0LYAOlBrV6vIx6PI51Ob9Vx6hDHR5ypTIpDOvVWr8Uk8zWBxq+3WCzYiVpLs5DKfP/95bDT6+u/vrF/AyJ3rTrk7XZeAAAAAElFTkSuQmCC')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not overload the user with too many input controls",
              "title": "Do not overload the user with too many input controls",
              "src": "/static/bd776a1e57c03bc9a38327041de779c9/377f4/do_not_columns.png",
              "srcSet": ["/static/bd776a1e57c03bc9a38327041de779c9/7fc1e/do_not_columns.png 288w", "/static/bd776a1e57c03bc9a38327041de779c9/a5df1/do_not_columns.png 576w", "/static/bd776a1e57c03bc9a38327041de779c9/377f4/do_not_columns.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span>
        </DoDontExample>
      </Column>
    </Row>
    <h2>{`Variants`}</h2>
    <p>{`As mentioned above, forms may be presented as dedicated pages, side panels, or dialogs depending on the use case and the situation.`}</p>
    <h3>{`Deciding what to use`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Form variant`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Context`}{`*`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Dedicated page`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For more complex, lengthier or multi-step requests for user input`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Creating a new service, such as provisioning; more complex order forms etc.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Dialog`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For critical, infrequent requests for user input often related to editing and management tasks`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User permissions; upgrading a service`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Side panel`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For repeated requests for user input which require the user needs to reference the affected information`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Calibrating row information in a data table`}</td>
        </tr>
      </tbody>
    </table>
    <Caption mdxType="Caption">
  * We are looking for more input from product teams here. Please connect with
  us about your use cases.
    </Caption>
    <h4>{`Dialog forms:`}</h4>
    <ul>
      <li parentName="ul">{`Use a dialog form when dealing with less than five inputs.`}</li>
      <li parentName="ul">{`Do not hide information in accordions or tabs.`}</li>
      <li parentName="ul">{`A `}<a parentName="li" {...{
          "href": "/patterns/dialog-pattern"
        }}>{`dialog pattern`}</a>{` with more detailed guidance will be released shortly.`}</li>
    </ul>
    <h4>{`Side panel forms:`}</h4>
    <ul>
      <li parentName="ul">{`Use a side panel form when dealing with more than five inputs.`}</li>
      <li parentName="ul">{`Do not hide information in accordions or tabs.`}</li>
    </ul>
    <h2>{`Accessibility`}</h2>
    <p>{`When constructing a form, first refer to the specific accessibility guidance for each component used. Every text input should have a descriptive and visible label, along with hard coded instructions for input format. A form must be wrapped in a `}<inlineCode parentName="p">{`<form>`}</inlineCode>{` element.`}</p>
    <p>{`Requirements for your form should be announced and declared before the user enters the form.`}</p>
    <p>{`The most significant challenge facing visually impaired users is form ordering. Your form should be tab-navigable, and required fields should be clearly labeled as such.`}</p>
    <p>{`Validation messages should be included to advise the user of data that is input incorrectly or a required field that is missing information.`}</p>
    <p>{`Helper text (`}<inlineCode parentName="p">{`label`}</inlineCode>{`) should be used to provide instructions to help users understand how to complete the form fields as well as indicate any required and optional input, data formats, and other relevant information.`}</p>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/tutorials/forms/instructions/"
      }}>{`WCAG website`}</a>{` for in-depth accessibility guidance for each form element.`}</p>
    <h2>{`Related`}</h2>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={3} mdxType="Column">
        <h4>{`Components`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/button/usage"
            }}>{`Button`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/checkbox/usage"
            }}>{`Checkbox`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/combobox--default"
            }}>{`Combo box`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/multiselect--default"
            }}>{`Multiselect`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/textinput--toggle-password-visibility"
            }}>{`Password input`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/radio-button/usage"
            }}>{`Radio button`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "http://react.carbondesignsystem.com/?path=/story/textarea--default"
            }}>{`Text area`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/text-input/usage"
            }}>{`Text input`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/toggle/usage"
            }}>{`Toggle`}</a>{` `}<br /></li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={3} mdxType="Column">
        <h4>{`Patterns`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/patterns/dialog-pattern"
            }}>{`Dialogs`}</a>{` `}<br /></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/patterns/notification-pattern"
            }}>{`Notifications`}</a>{` `}<br /></li>
        </ul>
      </Column>
    </Row>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul">{`Alita Joyce, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/tooltip-guidelines/"
        }}>{`Tooltip Guidelines`}</a>{`, (Nielsen Norman Group, 2019)`}</li>
      <li parentName="ul">{`Jakob Nielsen, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/ok-cancel-or-cancel-ok/"
        }}>{`OK-Cancel or Cancel-OK? The Trouble With Buttons`}</a>{`, (Nielsen Norman Group, 2008)`}</li>
      <li parentName="ul">{`Kathryn Whitenton, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/web-form-design/"
        }}>{`Website Forms Usability: Top 10 Recommendations`}</a>{`, (Nielsen Norman Group, 2016)`}</li>
      <li parentName="ul">{`Luke Wroblewski, `}<a parentName="li" {...{
          "href": "https://www.lukew.com/ff/entry.asp?1190"
        }}>{`Testing Accordion Forms`}</a>{`, (A List Apart, 2010)`}</li>
    </ul>
    <h3>{`Further reading`}</h3>
    <ul>
      <li parentName="ul">{`Andrew Coyle, `}<a parentName="li" {...{
          "href": "https://uxdesign.cc/design-better-forms-96fadca0f49c"
        }}>{`Design Better Forms`}</a>{`, (UX Collective, 2016)`}</li>
      <li parentName="ul">{`Hoa Loranger, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/form-design-white-space/"
        }}>{`Form Design Quick Fix: Group Form Elements Effectively Using White Space`}</a>{`, Nielsen Norman Group, 2013)`}</li>
      <li parentName="ul">{`Marieke McCloskey, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/accordions-complex-content/"
        }}>{`Accordions Are Not Always the Answer for Complex Content on Desktops`}</a>{`, Nielsen Norman Group, 2014)`}</li>
    </ul>
    <h2>{`Feedback`}</h2>
    <p>{`Help us improve this pattern by providing feedback, asking questions, and leaving any other comments on `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new?assignees=&labels=feedback&template=feedback.md"
      }}>{`GitHub`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      